/**
 * Standalone Hotels Portal - Cards Management
 * 
 * Admin page for managing NFC cards with engagement type selection.
 * Supports three engagement modes:
 * - Guest Arrival TAG (Check-In Survey) - GREEN
 * - Review Engine CARD (External Reviews) - AMBER
 * - Restroom Survey TAG (Facility Feedback) - BLUE
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  CreditCard,
  Plus,
  ArrowLeft,
  Copy,
  ExternalLink,
  ClipboardCheck,
  Star,
  Loader2,
  Check,
  MoreVertical,
  Trash2,
  UserPlus,
  Filter,
  Settings,
  Edit,
  Droplets,
  Image,
  Upload,
  X,
  AlertCircle,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
const API_URL = `${API_BASE}/api/hotels-standalone`;
// PUBLIC_URL is the production domain for NFC card programming (what guests access)
const PUBLIC_URL = process.env.REACT_APP_PUBLIC_URL || API_BASE;

export default function StandaloneCards() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const typeFilter = searchParams.get('type'); // 'guest_arrival' or 'tap_engine' or null
  
  const [loading, setLoading] = useState(true);
  const [cards, setCards] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [locations, setLocations] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [hotelUsers, setHotelUsers] = useState([]); // Users for alert assignment
  const [currentUserRole, setCurrentUserRole] = useState(''); // Current user's role
  const [selectedOrg, setSelectedOrg] = useState('');
  const [selectedOrgData, setSelectedOrgData] = useState(null);  // Store full org data for hotel_code
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [creating, setCreating] = useState(false);
  const [assigning, setAssigning] = useState(false);
  
  // Form states
  const [newCardId, setNewCardId] = useState('');
  const [newEngagementType, setNewEngagementType] = useState('tap_engine');
  const [newLocationId, setNewLocationId] = useState('none');
  const [assignCardId, setAssignCardId] = useState('');
  const [assignEngagementType, setAssignEngagementType] = useState('tap_engine');
  const [assignLocationId, setAssignLocationId] = useState('none');
  const [assignEmployeeId, setAssignEmployeeId] = useState('none');
  
  // Review URL editing (for tap_engine cards)
  const [editReviewUrl, setEditReviewUrl] = useState('');
  const [editDefaultPlatform, setEditDefaultPlatform] = useState('google');
  // Store ALL platform URLs so we can switch between them without losing data
  const [allPlatformUrls, setAllPlatformUrls] = useState({
    google: '',
    facebook: '',
    tripadvisor: '',
    yelp: '',
    trustpilot: '',
  });
  
  // Survey question state (for guest_arrival cards)
  const [surveyQuestions, setSurveyQuestions] = useState([]);
  
  // Alert assignment state (for guest_arrival, restroom, retailer_alert cards)
  const [alertAssignmentType, setAlertAssignmentType] = useState('none'); // 'none', 'user', 'custom'
  const [alertUserId, setAlertUserId] = useState('');
  const [alertCustomEmail, setAlertCustomEmail] = useState('');
  const [alertSelectedUserIds, setAlertSelectedUserIds] = useState([]);
  const [alertAdditionalEmails, setAlertAdditionalEmails] = useState('');
  const [allSystemUsers, setAllSystemUsers] = useState([]);
  
  // SMS alert state (per card)
  const [alertSmsRecipients, setAlertSmsRecipients] = useState([]); // [{phone_number, carrier, name}]
  const [smsPhoneInput, setSmsPhoneInput] = useState('');
  const [smsCarrierInput, setSmsCarrierInput] = useState('');
  const [smsNameInput, setSmsNameInput] = useState('');
  const [carrierList, setCarrierList] = useState([]);
  const [applySmToAllCards, setApplySmToAllCards] = useState(false);

  // Card-level logo branding state
  const [cardLogoUrl, setCardLogoUrl] = useState('');
  const [cardLabel, setCardLabel] = useState(''); // Descriptive label for card location (e.g., "Aisle 20 Front")
  const [cardLogoSize, setCardLogoSize] = useState('medium');
  const [cardLogoUrlInput, setCardLogoUrlInput] = useState('');
  const [uploadingCardLogo, setUploadingCardLogo] = useState(false);
  const [propertyHasLogo, setPropertyHasLogo] = useState(false);

  // Card-level wallpaper + card style state
  const [cardWallpaperUrl, setCardWallpaperUrl] = useState('');
  const [cardWallpaperUrlInput, setCardWallpaperUrlInput] = useState('');
  const [uploadingWallpaper, setUploadingWallpaper] = useState(false);
  const [cardStyle, setCardStyle] = useState('frosted');

  // Logo size mapping for preview
  const logoSizeClasses = {
    small: 'h-12 max-w-[100px]',
    medium: 'h-20 max-w-[160px]',
    large: 'h-28 max-w-[220px]',
    very_large: 'h-36 max-w-[280px]'
  };

  // Phone number auto-formatter: 5149999999 → 514-999-9999
  const formatPhoneInput = (value) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0,3)}-${digits.slice(3)}`;
    return `${digits.slice(0,3)}-${digits.slice(3,6)}-${digits.slice(6,10)}`;
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneInput(e.target.value);
    setSmsPhoneInput(formatted);
  };

  const addSmsRecipient = () => {
    const digits = smsPhoneInput.replace(/\D/g, '');
    if (digits.length < 10) { toast.error('Enter a valid 10-digit phone number'); return; }
    if (!smsCarrierInput) { toast.error('Select a carrier'); return; }
    setAlertSmsRecipients([...alertSmsRecipients, {
      phone_number: digits,
      carrier: smsCarrierInput,
      name: smsNameInput.trim(),
    }]);
    setSmsPhoneInput('');
    setSmsCarrierInput('');
    setSmsNameInput('');
  };

  const removeSmsRecipient = (index) => {
    setAlertSmsRecipients(alertSmsRecipients.filter((_, i) => i !== index));
  };

  // When platform changes, update the URL field to show that platform's URL
  // and save the current URL to the previous platform
  const handlePlatformChange = (newPlatform) => {
    // Save current URL to the current platform before switching
    setAllPlatformUrls(prev => ({
      ...prev,
      [editDefaultPlatform]: editReviewUrl
    }));
    // Switch to new platform and load its URL
    setEditDefaultPlatform(newPlatform);
    setEditReviewUrl(allPlatformUrls[newPlatform] || '');
  };

  // When URL changes, update it in allPlatformUrls for the current platform
  const handleUrlChange = (newUrl) => {
    setEditReviewUrl(newUrl);
    setAllPlatformUrls(prev => ({
      ...prev,
      [editDefaultPlatform]: newUrl
    }));
  };

  // Get the hotel code prefix for the selected organization
  const getHotelCodePrefix = () => {
    if (selectedOrgData && selectedOrgData.hotel_code) {
      return `${selectedOrgData.hotel_code}-`;
    }
    return '';
  };

  // Accept either standalone token or main portal token
  const token = localStorage.getItem('hotels_token') || localStorage.getItem('token');

  // Force dark mode for standalone pages
  useEffect(() => {
    document.documentElement.classList.add('dark');
    return () => {
      const savedTheme = localStorage.getItem('yul_theme_preference');
      if (savedTheme !== 'dark') {
        document.documentElement.classList.remove('dark');
      }
    };
  }, []);

  useEffect(() => {
    if (!token) {
      navigate('/standalone/login');
      return;
    }
    fetchOrganizations();
    fetchCurrentUser();
    // Load carrier list for SMS
    fetch(`${API_URL}/carriers`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(setCarrierList)
      .catch(() => {});
  }, [token, navigate]);

  useEffect(() => {
    if (selectedOrg) {
      fetchCards();
      fetchLocations();
      fetchEmployees();
    }
  }, [selectedOrg]);

  const fetchOrganizations = async () => {
    try {
      const res = await fetch(`${API_URL}/organizations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to fetch organizations');
      const data = await res.json();
      setOrganizations(data);
      if (data.length === 1) {
        setSelectedOrg(data[0].id);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to load organizations');
      setLoading(false);
    }
  };

  const fetchCards = async () => {
    try {
      const res = await fetch(`${API_URL}/organizations/${selectedOrg}/cards`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to fetch cards');
      const data = await res.json();
      setCards(data);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to load cards');
    }
  };

  const fetchLocations = async () => {
    try {
      const res = await fetch(`${API_URL}/organizations/${selectedOrg}/locations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to fetch locations');
      const data = await res.json();
      setLocations(data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchEmployees = async () => {
    try {
      const res = await fetch(`${API_URL}/organizations/${selectedOrg}/employees`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to fetch employees');
      const data = await res.json();
      setEmployees(data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchHotelUsers = async (orgId) => {
    try {
      // Fetch users assigned to this hotel for alert assignment
      const res = await fetch(`${API_BASE}/api/hotels/${orgId}/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to fetch hotel users');
      const data = await res.json();
      setHotelUsers(data.users || []);
    } catch (error) {
      console.error('Error fetching hotel users:', error);
      setHotelUsers([]);
    }
    
    // Also fetch all system users (includes super admins)
    try {
      const allRes = await fetch(`${API_URL}/users/all`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (allRes.ok) {
        const allData = await allRes.json();
        setAllSystemUsers(allData.users || allData || []);
      }
    } catch (error) {
      console.error('Error fetching all users:', error);
    }
  };

  const fetchCurrentUser = async () => {
    try {
      const res = await fetch(`${API_URL}/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCurrentUserRole(data.role || '');
      }
    } catch (error) {
      console.error('Error fetching current user:', error);
    }
  };

  const handleCreateCard = async () => {
    if (!newCardId.trim()) {
      toast.error('Card ID is required');
      return;
    }

    // Include hotel code prefix in the card ID
    const fullCardId = `${getHotelCodePrefix()}${newCardId.trim()}`;

    setCreating(true);
    try {
      const res = await fetch(`${API_URL}/organizations/${selectedOrg}/cards`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          card_id: fullCardId,
          engagement_type: newEngagementType,
          location_id: newLocationId === 'none' ? null : newLocationId
        })
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to create card');
      }

      toast.success('Card created successfully');
      setShowCreateModal(false);
      setNewCardId('');
      setNewEngagementType('tap_engine');
      setNewLocationId('none');
      fetchCards();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setCreating(false);
    }
  };

  const handleAssignCard = async () => {
    if (!selectedCard) return;
    if (!assignCardId.trim()) {
      toast.error('Card ID is required');
      return;
    }

    setAssigning(true);
    try {
      // Update the card
      const res = await fetch(`${API_URL}/cards/${selectedCard.card_id}/update`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          new_card_id: assignCardId.trim(),
          engagement_type: assignEngagementType,
          location_id: assignLocationId === 'none' ? null : assignLocationId,
          employee_id: assignEmployeeId === 'none' ? null : assignEmployeeId,
          card_label: cardLabel.trim() || null
        })
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to update card');
      }

      // Save alert assignment for guest_arrival, restroom, AND retailer_alert cards
      if ((assignEngagementType === 'guest_arrival' || assignEngagementType === 'restroom' || assignEngagementType === 'retailer_alert') && selectedCard.organization_id) {
        try {
          // Determine alert recipient(s)
          const alertData = {};
          if (alertAssignmentType === 'user' && alertUserId) {
            alertData.alert_user_id = alertUserId;
            alertData.alert_email = null;
          } else if (alertAssignmentType === 'custom' && alertCustomEmail.trim()) {
            alertData.alert_email = alertCustomEmail.trim();
            alertData.alert_user_id = null;
          } else if (alertAssignmentType === 'none') {
            alertData.alert_user_id = null;
            alertData.alert_email = null;
          }
          
          // For restroom and retailer_alert cards, also check the employee dropdown as fallback
          if ((assignEngagementType === 'restroom' || assignEngagementType === 'retailer_alert') && assignEmployeeId !== 'none' && !alertData.alert_user_id) {
            alertData.alert_user_id = assignEmployeeId;
          }
          
          // Add multi-recipient data
          alertData.alert_user_ids = alertSelectedUserIds.length > 0 ? alertSelectedUserIds : [];
          const parsedAdditionalEmails = alertAdditionalEmails.split(',').map(e => e.trim()).filter(e => e && e.includes('@'));
          alertData.alert_emails = parsedAdditionalEmails;
          alertData.alert_sms_recipients = alertSmsRecipients;

          // Always save alert settings (even if clearing them)
          await fetch(`${API_URL}/cards/${assignCardId.trim()}/update`, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              ...alertData,
              engagement_type: assignEngagementType
            })
          });
          
          // If "Apply SMS to all cards" is checked, update all cards for this org
          if (applySmToAllCards && alertSmsRecipients.length > 0 && selectedCard.organization_id) {
            const orgCards = cards.filter(c => c.organization_id === selectedCard.organization_id && c.card_id !== assignCardId.trim());
            for (const c of orgCards) {
              try {
                await fetch(`${API_URL}/cards/${c.card_id}/update`, {
                  method: 'PUT',
                  headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                  body: JSON.stringify({ alert_sms_recipients: alertSmsRecipients })
                });
              } catch (e) { console.error('Failed to update card:', c.card_id); }
            }
            toast.success(`SMS recipients applied to ${orgCards.length + 1} cards`);
          }
        } catch (alertErr) {
          console.error('Error saving alert settings:', alertErr);
          toast.error('Card updated but failed to save alert settings');
        }
      }

      // If tap_engine card and review URL was edited, save it to the hotel
      if (assignEngagementType === 'tap_engine' && selectedCard.organization_id) {
        try {
          // Make sure current URL is saved to allPlatformUrls
          const finalUrls = {
            ...allPlatformUrls,
            [editDefaultPlatform]: editReviewUrl
          };
          
          // Build the config objects - ONLY include URLs that have values
          const reviewsConfig = {
            enabled: true,
            default_platform: editDefaultPlatform,
          };
          const tapEngineConfig = {
            default_platform: editDefaultPlatform,
          };
          
          // Add all non-empty URLs to preserve existing data
          if (finalUrls.google && finalUrls.google.trim()) {
            reviewsConfig.google_url = finalUrls.google;
            tapEngineConfig.google_reviews_url = finalUrls.google;
          }
          if (finalUrls.facebook && finalUrls.facebook.trim()) {
            reviewsConfig.facebook_url = finalUrls.facebook;
            tapEngineConfig.facebook_url = finalUrls.facebook;
          }
          if (finalUrls.tripadvisor && finalUrls.tripadvisor.trim()) {
            reviewsConfig.tripadvisor_url = finalUrls.tripadvisor;
            tapEngineConfig.tripadvisor_url = finalUrls.tripadvisor;
          }
          if (finalUrls.yelp && finalUrls.yelp.trim()) {
            reviewsConfig.yelp_url = finalUrls.yelp;
            tapEngineConfig.yelp_url = finalUrls.yelp;
          }
          if (finalUrls.trustpilot && finalUrls.trustpilot.trim()) {
            reviewsConfig.trustpilot_url = finalUrls.trustpilot;
            tapEngineConfig.trustpilot_url = finalUrls.trustpilot;
          }
          
          // Use the standard hotels API update endpoint
          const urlRes = await fetch(`${API_BASE}/api/hotels/${selectedCard.organization_id}`, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              engagement_modes: {
                reviews: reviewsConfig,
                tap_engine_urls: tapEngineConfig,
              }
            })
          });
          
          if (urlRes.ok) {
            toast.success('Review settings updated');
          }
        } catch (urlErr) {
          console.error('Error saving review URL:', urlErr);
          toast.error('Card updated but failed to save review URL');
        }
      }

      toast.success('Card updated successfully');
      setShowAssignModal(false);
      setSelectedCard(null);
      setEditReviewUrl('');
      // Reset alert assignment state
      setAlertAssignmentType('none');
      setAlertUserId('');
      setAlertCustomEmail('');
      // Reset logo state
      setCardLogoUrl('');
      setCardLogoSize('medium');
      setCardLogoUrlInput('');
      setPropertyHasLogo(false);
      fetchCards();
      fetchOrganizations(); // Refresh to get updated URLs
    } catch (error) {
      toast.error(error.message);
    } finally {
      setAssigning(false);
    }
  };

  // Handle card-level logo upload
  const handleCardLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const allowedTypes = ['image/png', 'image/jpeg', 'image/gif', 'image/webp', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a PNG, JPEG, GIF, WebP, or SVG image');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB');
      return;
    }
    
    setUploadingCardLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await fetch(`${API_URL}/upload/logo`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (!res.ok) throw new Error('Upload failed');
      
      const data = await res.json();
      setCardLogoUrl(data.url);
      toast.success('Logo uploaded');
    } catch (error) {
      toast.error('Failed to upload logo');
    } finally {
      setUploadingCardLogo(false);
    }
  };

  // Save card branding
  const saveCardBranding = async () => {
    if (!selectedCard) return;
    
    try {
      const res = await fetch(`${API_URL}/cards/${assignCardId.trim()}/branding`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          logo_url: cardLogoUrl || null,
          logo_size: cardLogoSize,
          wallpaper_url: cardWallpaperUrl || null,
          card_style: cardStyle || 'frosted'
        })
      });
      
      if (!res.ok) throw new Error('Failed to save branding');
      toast.success('Card branding saved');
      fetchCards();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleWallpaperUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a PNG, JPEG, GIF, or WebP image');
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 8MB');
      return;
    }

    setUploadingWallpaper(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await fetch(`${API_URL}/upload/logo`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (!res.ok) throw new Error('Upload failed');
      const data = await res.json();
      setCardWallpaperUrl(data.url);
      toast.success('Wallpaper uploaded');
    } catch (error) {
      toast.error('Failed to upload wallpaper');
    } finally {
      setUploadingWallpaper(false);
    }
  };

  const handleUnassignCard = async (card) => {
    try {
      const res = await fetch(`${API_URL}/cards/${card.card_id}/unassign`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!res.ok) throw new Error('Failed to unassign card');
      toast.success('Card unassigned');
      fetchCards();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const copyNfcUrl = (cardId, engagementType) => {
    // Use explicit URL based on card type
    let urlPath;
    if (engagementType === 'in_room_survey' || engagementType === 'guest_arrival') {
      urlPath = `/standalone/room/${cardId}`;
    } else if (engagementType === 'restroom') {
      urlPath = `/standalone/restroom/${cardId}`;
    } else {
      urlPath = `/standalone/review/${cardId}`;
    }
    const url = `${PUBLIC_URL}${urlPath}`;
    navigator.clipboard.writeText(url);
    toast.success('NFC URL copied to clipboard');
  };

  const openAssignModal = async (card) => {
    setSelectedCard(card);
    setAssignCardId(card.card_id);
    setAssignEngagementType(card.engagement_type || 'tap_engine');
    setAssignLocationId(card.location_id || 'none');
    setAssignEmployeeId(card.employee_id || 'none');
    
    // Load organization/hotel data to get review URLs
    const org = organizations.find(o => o.id === card.organization_id);
    if (org) {
      setSelectedOrgData(org);
      // Get the default platform
      const defaultPlatform = org.platform_urls?.default_platform || 'google';
      setEditDefaultPlatform(defaultPlatform);
      
      // Load ALL platform URLs so we can switch between them
      const platformUrls = {
        google: org.platform_urls?.google_reviews_url || org.platform_urls?.google || '',
        facebook: org.platform_urls?.facebook_url || org.platform_urls?.facebook || '',
        tripadvisor: org.platform_urls?.tripadvisor_url || org.platform_urls?.tripadvisor || '',
        yelp: org.platform_urls?.yelp_url || org.platform_urls?.yelp || '',
        trustpilot: org.platform_urls?.trustpilot_url || org.platform_urls?.trustpilot || '',
      };
      setAllPlatformUrls(platformUrls);
      
      // Set the URL field to show the current default platform's URL
      setEditReviewUrl(platformUrls[defaultPlatform] || '');
    }
    
    // Load survey questions for guest_arrival cards
    if (card.engagement_type === 'guest_arrival' || !card.engagement_type) {
      try {
        const res = await fetch(`${API_URL}/survey/${card.organization_id}/default?lang=en`);
        if (res.ok) {
          const data = await res.json();
          setSurveyQuestions(data.questions || []);
        }
      } catch (err) {
        console.error('Error loading survey questions:', err);
        setSurveyQuestions([]);
      }
    }
    
    // Fetch hotel users for alert assignment
    if (card.organization_id) {
      await fetchHotelUsers(card.organization_id);
    }
    
    // Reset alert assignment state and load existing values from card
    setAlertAssignmentType(
      card.alert_user_id ? 'user' : 
      card.alert_email ? 'custom' : 'none'
    );
    setAlertUserId(card.alert_user_id || '');
    setAlertCustomEmail(card.alert_email || '');
    setAlertSelectedUserIds(card.alert_user_ids || []);
    setAlertAdditionalEmails((card.alert_emails || []).join(', '));
    setAlertSmsRecipients(card.alert_sms_recipients || []);
    setApplySmToAllCards(false);
    setCardLabel(card.card_label || '');
    
    // Load card-level branding
    const cardBranding = card.branding || {};
    setCardLogoUrl(cardBranding.logo_url || '');
    setCardLogoSize(cardBranding.logo_size || 'medium');
    setCardLogoUrlInput('');
    setCardWallpaperUrl(cardBranding.wallpaper_url || '');
    setCardWallpaperUrlInput('');
    setCardStyle(cardBranding.card_style || 'frosted');
    
    // Check if property has a logo (for display info)
    if (org) {
      const propBranding = org.survey_branding || {};
      setPropertyHasLogo(!!propBranding.property_logo_url && propBranding.use_property_logo_for_all);
    } else {
      setPropertyHasLogo(false);
    }
    
    setShowAssignModal(true);
  };

  // Filter cards based on typeFilter
  const filteredCards = typeFilter
    ? cards.filter(card => (card.engagement_type || 'tap_engine') === typeFilter)
    : cards;

  const getPageTitle = () => {
    if (typeFilter === 'guest_arrival') return 'Room Survey Tags';
    if (typeFilter === 'restroom') return 'Restroom Survey Tags';
    if (typeFilter === 'tap_engine') return 'Review Engine Cards';
    if (typeFilter === 'retailer_alert') return 'Retailer Alert Tags';
    return 'NFC Cards';
  };

  const getPageSubtitle = () => {
    if (typeFilter === 'guest_arrival') return 'In-room tags for internal guest feedback surveys';
    if (typeFilter === 'restroom') return 'Restroom tags for facility feedback surveys';
    if (typeFilter === 'tap_engine') return 'Standalone cards to drive Google, TripAdvisor & Facebook reviews';
    if (typeFilter === 'retailer_alert') return 'In-store tags for category-based incident reporting';
    return 'Manage Room Survey, Restroom Survey, Review Engine & Retailer Alert Cards';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[hsl(222,47%,11%)] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-white animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[hsl(222,47%,11%)]">
      {/* Header */}
      <header className="bg-[hsl(217,19%,12%)] border-b border-[hsl(217,19%,27%)] px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/hotels')}
              className="text-[hsl(215,20%,65%)] hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Properties
            </Button>
            <div className="flex items-center space-x-3">
              {typeFilter === 'guest_arrival' && (
                <div className="w-10 h-10 rounded-lg bg-green-600/20 flex items-center justify-center">
                  <ClipboardCheck className="w-5 h-5 text-green-400" />
                </div>
              )}
              {typeFilter === 'restroom' && (
                <div className="w-10 h-10 rounded-lg bg-blue-600/20 flex items-center justify-center">
                  <Droplets className="w-5 h-5 text-blue-400" />
                </div>
              )}
              {typeFilter === 'tap_engine' && (
                <div className="w-10 h-10 rounded-lg bg-amber-600/20 flex items-center justify-center">
                  <Star className="w-5 h-5 text-amber-400" />
                </div>
              )}
              {typeFilter === 'retailer_alert' && (
                <div className="w-10 h-10 rounded-lg bg-fuchsia-600/20 flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-fuchsia-400" />
                </div>
              )}
              <div>
                <h1 className="text-xl font-bold text-white">{getPageTitle()}</h1>
                <p className="text-sm text-[hsl(215,20%,65%)]">{getPageSubtitle()}</p>
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/standalone/dashboard')}
              className="text-[hsl(215,20%,65%)] hover:text-white"
            >
              Overview
            </Button>
            <Button
              className="bg-blue-600 hover:bg-blue-700"
              onClick={() => setShowCreateModal(true)}
              disabled={!selectedOrg}
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Card
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Organization Selector */}
        {organizations.length > 1 && (
          <div className="mb-6">
            <Label className="text-white mb-2 block">Select Hotel</Label>
            <Select 
              value={selectedOrg} 
              onValueChange={(value) => {
                setSelectedOrg(value);
                const org = organizations.find(o => o.id === value);
                setSelectedOrgData(org || null);
              }}
            >
              <SelectTrigger className="w-full max-w-md bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)] text-white">
                <SelectValue placeholder="Select hotel" />
              </SelectTrigger>
              <SelectContent>
                {organizations.map((org) => (
                  <SelectItem key={org.id} value={org.id}>
                    {org.name} {org.hotel_code ? `(${org.hotel_code})` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {!selectedOrg ? (
          <Card className="bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)]">
            <CardContent className="pt-6 text-center">
              <p className="text-[hsl(215,20%,65%)]">Please select an organization to view cards</p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Filter Tabs */}
            <div className="flex items-center space-x-2 mb-6 flex-wrap gap-y-2">
              <Button
                variant={!typeFilter ? "default" : "outline"}
                size="sm"
                onClick={() => setSearchParams({})}
                className={!typeFilter 
                  ? "bg-white/10 text-white" 
                  : "border-[hsl(217,19%,27%)] text-[hsl(215,20%,65%)] hover:text-white hover:bg-[hsl(217,19%,27%)]"
                }
              >
                All Cards ({cards.length})
              </Button>
              <Button
                variant={typeFilter === 'guest_arrival' ? "default" : "outline"}
                size="sm"
                onClick={() => setSearchParams({ type: 'guest_arrival' })}
                className={typeFilter === 'guest_arrival'
                  ? "bg-green-600/30 text-green-400 border-green-600/40"
                  : "border-[hsl(217,19%,27%)] text-[hsl(215,20%,65%)] hover:text-green-400 hover:bg-green-600/10 hover:border-green-600/40"
                }
              >
                <ClipboardCheck className="w-3 h-3 mr-1" />
                Room Survey ({cards.filter(c => c.engagement_type === 'guest_arrival').length})
              </Button>
              <Button
                variant={typeFilter === 'restroom' ? "default" : "outline"}
                size="sm"
                onClick={() => setSearchParams({ type: 'restroom' })}
                className={typeFilter === 'restroom'
                  ? "bg-blue-600/30 text-blue-400 border-blue-600/40"
                  : "border-[hsl(217,19%,27%)] text-[hsl(215,20%,65%)] hover:text-blue-400 hover:bg-blue-600/10 hover:border-blue-600/40"
                }
              >
                <Droplets className="w-3 h-3 mr-1" />
                Restroom Survey ({cards.filter(c => c.engagement_type === 'restroom').length})
              </Button>
              <Button
                variant={typeFilter === 'tap_engine' ? "default" : "outline"}
                size="sm"
                onClick={() => setSearchParams({ type: 'tap_engine' })}
                className={typeFilter === 'tap_engine'
                  ? "bg-amber-600/30 text-amber-400 border-amber-600/40"
                  : "border-[hsl(217,19%,27%)] text-[hsl(215,20%,65%)] hover:text-amber-400 hover:bg-amber-600/10 hover:border-amber-600/40"
                }
              >
                <Star className="w-3 h-3 mr-1" />
                Review Engine ({cards.filter(c => (c.engagement_type || 'tap_engine') === 'tap_engine').length})
              </Button>
              <Button
                variant={typeFilter === 'retailer_alert' ? "default" : "outline"}
                size="sm"
                onClick={() => setSearchParams({ type: 'retailer_alert' })}
                className={typeFilter === 'retailer_alert'
                  ? "bg-fuchsia-600/30 text-fuchsia-400 border-fuchsia-600/40"
                  : "border-[hsl(217,19%,27%)] text-[hsl(215,20%,65%)] hover:text-fuchsia-400 hover:bg-fuchsia-600/10 hover:border-fuchsia-600/40"
                }
              >
                <AlertCircle className="w-3 h-3 mr-1" />
                Retailer Alert ({cards.filter(c => c.engagement_type === 'retailer_alert').length})
              </Button>
            </div>

            {/* Cards List */}
            <div className="grid gap-4">
              {filteredCards.length === 0 ? (
                <Card className="bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)]">
                  <CardContent className="pt-6 text-center">
                    {typeFilter === 'guest_arrival' ? (
                      <ClipboardCheck className="w-12 h-12 text-green-400/50 mx-auto mb-4" />
                    ) : typeFilter === 'restroom' ? (
                      <Droplets className="w-12 h-12 text-blue-400/50 mx-auto mb-4" />
                    ) : typeFilter === 'tap_engine' ? (
                      <Star className="w-12 h-12 text-amber-400/50 mx-auto mb-4" />
                    ) : typeFilter === 'retailer_alert' ? (
                      <AlertCircle className="w-12 h-12 text-fuchsia-400/50 mx-auto mb-4" />
                    ) : (
                      <CreditCard className="w-12 h-12 text-[hsl(215,20%,65%)] mx-auto mb-4" />
                    )}
                    <p className="text-white font-medium mb-2">
                      {typeFilter 
                        ? `No ${typeFilter === 'guest_arrival' ? 'Room Survey Tags' : typeFilter === 'restroom' ? 'Restroom Survey Tags' : typeFilter === 'retailer_alert' ? 'Retailer Alert Tags' : 'Review Engine Cards'} yet` 
                        : 'No cards yet'}
                    </p>
                    <p className="text-[hsl(215,20%,65%)] text-sm mb-4">
                      {typeFilter 
                        ? `Create a new ${typeFilter === 'guest_arrival' ? 'Room Survey Tag' : typeFilter === 'restroom' ? 'Restroom Survey Tag' : typeFilter === 'retailer_alert' ? 'Retailer Alert Tag' : 'Review Engine Card'} to get started`
                        : 'Create your first NFC card to get started'
                      }
                    </p>
                    <Button
                      className="bg-blue-600 hover:bg-blue-700"
                      onClick={() => setShowCreateModal(true)}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Create Card
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                filteredCards.map((card) => (
                  <Card
                    key={card.id || card.card_id}
                    className="bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)]"
                  >
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          {/* Engagement Type Icon */}
                          <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                            card.engagement_type === 'guest_arrival'
                              ? 'bg-green-600/20'
                              : card.engagement_type === 'restroom'
                              ? 'bg-blue-600/20'
                              : card.engagement_type === 'retailer_alert'
                              ? 'bg-fuchsia-600/20'
                              : 'bg-amber-600/20'
                          }`}>
                            {card.engagement_type === 'guest_arrival' ? (
                              <ClipboardCheck className="w-6 h-6 text-green-400" />
                            ) : card.engagement_type === 'restroom' ? (
                              <Droplets className="w-6 h-6 text-blue-400" />
                            ) : card.engagement_type === 'retailer_alert' ? (
                              <AlertCircle className="w-6 h-6 text-fuchsia-400" />
                            ) : (
                              <Star className="w-6 h-6 text-amber-400" />
                            )}
                          </div>
                          
                          <div>
                            <div className="flex items-center space-x-2">
                              <p className="text-white font-medium">{card.card_id}{card.card_label ? ` — ${card.card_label}` : ''}</p>
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                card.status === 'active'
                                  ? 'bg-green-600/20 text-green-400'
                                  : 'bg-yellow-600/20 text-yellow-400'
                              }`}>
                                {card.status}
                              </span>
                            </div>
                            <p className="text-sm text-[hsl(215,20%,65%)]">
                              {card.engagement_type === 'guest_arrival'
                                ? 'Room Survey Tag (Guest Feedback)'
                                : card.engagement_type === 'restroom'
                                ? 'Restroom Survey Tag (Facility Feedback)'
                                : card.engagement_type === 'retailer_alert'
                                ? 'Retailer Alert Tag (In-Store Reporting)'
                                : 'Review Engine Card (External Reviews)'}
                            </p>
                            {card.location_name && (
                              <p className="text-xs text-[hsl(215,20%,50%)]">
                                Location: {card.location_name}
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center space-x-2">
                          {/* Quick Activate button for unassigned cards */}
                          {card.status === 'unassigned' && (
                            <Button
                              size="sm"
                              onClick={() => openAssignModal(card)}
                              className="bg-green-600 hover:bg-green-700 text-white text-xs"
                            >
                              Activate
                            </Button>
                          )}
                          
                          {/* Edit button - always visible */}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openAssignModal(card)}
                            className="border-[hsl(217,19%,27%)] text-[hsl(215,20%,65%)] hover:text-white hover:bg-[hsl(217,19%,27%)]"
                            data-testid={`edit-card-${card.card_id}`}
                          >
                            <Edit className="w-3 h-3 mr-1" />
                            Edit
                          </Button>
                          
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => copyNfcUrl(card.card_id, card.engagement_type)}
                            className="text-[hsl(215,20%,65%)] hover:text-white"
                            data-testid={`copy-url-${card.card_id}`}
                            title="Copy NFC URL"
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                          
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-[hsl(215,20%,65%)] hover:text-white"
                              >
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => openAssignModal(card)}>
                                <Settings className="w-4 h-4 mr-2" />
                                Edit Card Settings
                              </DropdownMenuItem>
                              {card.status === 'unassigned' && (
                                <DropdownMenuItem onClick={() => openAssignModal(card)}>
                                  <UserPlus className="w-4 h-4 mr-2" />
                                  Activate Card
                                </DropdownMenuItem>
                              )}
                              {card.status === 'active' && (
                                <DropdownMenuItem
                                  onClick={() => handleUnassignCard(card)}
                                  className="text-red-400"
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Deactivate Card
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </>
        )}
      </main>

      {/* Create Card Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)] text-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create NFC Card</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-2">
            {/* Card ID with Hotel Code Prefix */}
            <div className="space-y-2">
              <Label className="text-white">Card ID</Label>
              {selectedOrgData?.hotel_code ? (
                <div className="flex items-center space-x-2">
                  <span className="text-white font-mono bg-blue-600/30 px-2 py-1.5 rounded border border-blue-500/50">
                    {selectedOrgData.hotel_code}-
                  </span>
                  <Input
                    value={newCardId}
                    onChange={(e) => setNewCardId(e.target.value.toUpperCase())}
                    placeholder="101, LOBBY, POOL"
                    className="bg-[hsl(222,47%,11%)] border-[hsl(217,19%,27%)] text-white flex-1"
                    data-testid="create-card-id-input"
                  />
                </div>
              ) : (
                <Input
                  value={newCardId}
                  onChange={(e) => setNewCardId(e.target.value)}
                  placeholder="e.g., SHERMTL-101, LOBBY"
                  className="bg-[hsl(222,47%,11%)] border-[hsl(217,19%,27%)] text-white"
                  data-testid="create-card-id-input"
                />
              )}
              <p className="text-xs text-[hsl(215,20%,65%)]">
                {selectedOrgData?.hotel_code 
                  ? `Enter room/location identifier. Hotel code "${selectedOrgData.hotel_code}" will be automatically added.`
                  : 'Enter a unique card identifier. Set up a Hotel Code in hotel settings for automatic prefixing.'}
              </p>
              
              {/* Live URL Preview - Uses explicit route based on engagement type */}
              {newCardId.trim() && (
                <div className="mt-2 p-3 rounded-lg bg-[hsl(222,47%,11%)] border border-[hsl(217,19%,27%)]">
                  <p className="text-xs text-[hsl(215,20%,65%)] mb-1">NFC URL to program:</p>
                  <div className="flex items-center space-x-2">
                    <code className={`flex-1 text-xs font-mono break-all ${
                      newEngagementType === 'guest_arrival' ? 'text-green-400' : 
                      newEngagementType === 'restroom' ? 'text-blue-400' : 
                      newEngagementType === 'retailer_alert' ? 'text-fuchsia-400' : 'text-amber-400'
                    }`}>
                      {PUBLIC_URL}/standalone/{
                        newEngagementType === 'in_room_survey' || newEngagementType === 'guest_arrival' ? 'room' : 
                        newEngagementType === 'restroom' ? 'restroom' : 
                        newEngagementType === 'retailer_alert' ? 'retailer-alert' : 'review'
                      }/{getHotelCodePrefix()}{newCardId.trim()}
                    </code>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const urlPath = newEngagementType === 'in_room_survey' || newEngagementType === 'guest_arrival' ? 'room' : 
                                        newEngagementType === 'restroom' ? 'restroom' : 
                                        newEngagementType === 'retailer_alert' ? 'retailer-alert' : 'review';
                        navigator.clipboard.writeText(`${PUBLIC_URL}/standalone/${urlPath}/${getHotelCodePrefix()}${newCardId.trim()}`);
                        toast.success('URL copied to clipboard');
                      }}
                      className="text-[hsl(215,20%,65%)] hover:text-white shrink-0 h-6 w-6 p-0"
                    >
                      <Copy className="w-3 h-3" />
                    </Button>
                  </div>
                  <p className={`text-xs mt-1 ${
                    newEngagementType === 'guest_arrival' ? 'text-green-400' : 
                    newEngagementType === 'restroom' ? 'text-blue-400' : 
                    newEngagementType === 'retailer_alert' ? 'text-fuchsia-400' : 'text-amber-400'
                  }`}>
                    {newEngagementType === 'in_room_survey' || newEngagementType === 'guest_arrival' 
                      ? 'Room Tag URL (Guest Arrival Survey)' 
                      : newEngagementType === 'restroom'
                      ? 'Restroom Tag URL (Facility Feedback Survey)'
                      : newEngagementType === 'retailer_alert'
                      ? 'Retailer Alert Tag URL (In-Store Reporting)'
                      : 'Review Engine Card URL'}
                  </p>
                  {/* Show what happens when tapped */}
                  <div className={`mt-2 pt-2 border-t border-[hsl(217,19%,27%)] flex items-center space-x-2 ${
                    newEngagementType === 'guest_arrival' ? 'text-green-400' : 
                    newEngagementType === 'restroom' ? 'text-blue-400' : 
                    newEngagementType === 'retailer_alert' ? 'text-fuchsia-400' : 'text-amber-400'
                  }`}>
                    {newEngagementType === 'guest_arrival' ? (
                      <>
                        <ClipboardCheck className="w-4 h-4" />
                        <span className="text-xs">When tapped → Guest will see <strong>Arrival Survey</strong></span>
                      </>
                    ) : newEngagementType === 'restroom' ? (
                      <>
                        <Droplets className="w-4 h-4" />
                        <span className="text-xs">When tapped → Guest will see <strong>Restroom Feedback Survey</strong></span>
                      </>
                    ) : newEngagementType === 'retailer_alert' ? (
                      <>
                        <AlertCircle className="w-4 h-4" />
                        <span className="text-xs">When tapped → Customer will see <strong>In-Store Reporting</strong> categories</span>
                      </>
                    ) : (
                      <>
                        <Star className="w-4 h-4" />
                        <span className="text-xs">When tapped → Guest will go to <strong>Review Platform</strong> (Google, etc.)</span>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Engagement Type Selector */}
            <div className="space-y-2">
              <Label className="text-white">Engagement Type</Label>
              <RadioGroup
                value={newEngagementType}
                onValueChange={setNewEngagementType}
                className="space-y-2"
                data-testid="create-engagement-type-radio"
              >
                <div className={`flex items-center space-x-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                  newEngagementType === 'guest_arrival'
                    ? 'border-green-500 bg-green-600/10'
                    : 'border-[hsl(217,19%,27%)] hover:border-[hsl(217,19%,40%)]'
                }`}>
                  <RadioGroupItem value="guest_arrival" id="new-guest-arrival" />
                  <label htmlFor="new-guest-arrival" className="flex-1 cursor-pointer flex items-center space-x-2 text-white">
                    <ClipboardCheck className="w-4 h-4 text-green-400" />
                    <span className="text-sm">Guest Arrival Tag (Room Survey)</span>
                  </label>
                </div>

                <div className={`flex items-center space-x-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                  newEngagementType === 'restroom'
                    ? 'border-blue-500 bg-blue-600/10'
                    : 'border-[hsl(217,19%,27%)] hover:border-[hsl(217,19%,40%)]'
                }`}>
                  <RadioGroupItem value="restroom" id="new-restroom" />
                  <label htmlFor="new-restroom" className="flex-1 cursor-pointer flex items-center space-x-2 text-white">
                    <Droplets className="w-4 h-4 text-blue-400" />
                    <span className="text-sm">Restroom Survey Tag (Facility Feedback)</span>
                  </label>
                </div>

                <div className={`flex items-center space-x-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                  newEngagementType === 'tap_engine'
                    ? 'border-amber-500 bg-amber-600/10'
                    : 'border-[hsl(217,19%,27%)] hover:border-[hsl(217,19%,40%)]'
                }`}>
                  <RadioGroupItem value="tap_engine" id="new-tap-engine" />
                  <label htmlFor="new-tap-engine" className="flex-1 cursor-pointer flex items-center space-x-2 text-white">
                    <Star className="w-4 h-4 text-amber-400" />
                    <span className="text-sm">Review Engine Card (External Reviews)</span>
                  </label>
                </div>

                <div className={`flex items-center space-x-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                  newEngagementType === 'retailer_alert'
                    ? 'border-fuchsia-500 bg-fuchsia-600/10'
                    : 'border-[hsl(217,19%,27%)] hover:border-[hsl(217,19%,40%)]'
                }`}>
                  <RadioGroupItem value="retailer_alert" id="new-retailer-alert" />
                  <label htmlFor="new-retailer-alert" className="flex-1 cursor-pointer flex items-center space-x-2 text-white">
                    <AlertCircle className="w-4 h-4 text-fuchsia-400" />
                    <span className="text-sm">Retailer Alert Tag (In-Store Reporting)</span>
                  </label>
                </div>
              </RadioGroup>
            </div>

            {/* Location (Optional) */}
            <div className="space-y-2">
              <Label className="text-white">Location (Optional)</Label>
              <Select value={newLocationId} onValueChange={setNewLocationId}>
                <SelectTrigger className="bg-[hsl(222,47%,11%)] border-[hsl(217,19%,27%)] text-white">
                  <SelectValue placeholder="Select location" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {locations.map((loc) => (
                    <SelectItem key={loc.id} value={loc.id}>
                      {loc.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter className="mt-4">
            <Button
              variant="ghost"
              onClick={() => setShowCreateModal(false)}
              className="text-[hsl(215,20%,65%)]"
            >
              Cancel
            </Button>
            <Button
              className="bg-blue-600 hover:bg-blue-700"
              onClick={handleCreateCard}
              disabled={creating || !newCardId.trim()}
              data-testid="create-card-submit"
            >
              {creating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Create Card
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Card Modal */}
      <Dialog open={showAssignModal} onOpenChange={setShowAssignModal}>
        <DialogContent className="bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)] text-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Card</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-2">
            {/* Card ID */}
            <div className="space-y-2">
              <Label className="text-white">Card ID / Room Number</Label>
              <Input
                value={assignCardId}
                onChange={(e) => setAssignCardId(e.target.value)}
                placeholder="e.g., 105, ROOM101, LOBBY, RESTROOM-1"
                className="bg-[hsl(222,47%,11%)] border-[hsl(217,19%,27%)] text-white"
                data-testid="edit-card-id-input"
              />
              
              {/* Live URL Preview - Uses explicit route based on engagement type */}
              {assignCardId.trim() && (
                <div className="mt-2 p-2 rounded-lg bg-[hsl(222,47%,11%)] border border-[hsl(217,19%,27%)]">
                  <p className="text-xs text-[hsl(215,20%,65%)] mb-1">NFC URL to program:</p>
                  <div className="flex items-center space-x-2">
                    <code className={`flex-1 text-xs font-mono break-all ${
                      assignEngagementType === 'guest_arrival' ? 'text-green-400' : 
                      assignEngagementType === 'restroom' ? 'text-blue-400' : 
                      assignEngagementType === 'retailer_alert' ? 'text-fuchsia-400' : 'text-amber-400'
                    }`}>
                      {PUBLIC_URL}/standalone/{
                        assignEngagementType === 'in_room_survey' || assignEngagementType === 'guest_arrival' ? 'room' : 
                        assignEngagementType === 'restroom' ? 'restroom' : 
                        assignEngagementType === 'retailer_alert' ? 'retailer-alert' : 'review'
                      }/{assignCardId.trim()}
                    </code>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const urlPath = assignEngagementType === 'in_room_survey' || assignEngagementType === 'guest_arrival' ? 'room' : 
                                        assignEngagementType === 'restroom' ? 'restroom' : 
                                        assignEngagementType === 'retailer_alert' ? 'retailer-alert' : 'review';
                        navigator.clipboard.writeText(`${PUBLIC_URL}/standalone/${urlPath}/${assignCardId.trim()}`);
                        toast.success('URL copied to clipboard');
                      }}
                      className="text-[hsl(215,20%,65%)] hover:text-white shrink-0 h-6 w-6 p-0"
                    >
                      <Copy className="w-3 h-3" />
                    </Button>
                  </div>
                  <p className={`text-xs mt-1 ${
                    assignEngagementType === 'guest_arrival' ? 'text-green-400' : 
                    assignEngagementType === 'restroom' ? 'text-blue-400' : 
                    assignEngagementType === 'retailer_alert' ? 'text-fuchsia-400' : 'text-amber-400'
                  }`}>
                    {assignEngagementType === 'in_room_survey' || assignEngagementType === 'guest_arrival' 
                      ? 'Room Survey Tag' 
                      : assignEngagementType === 'restroom'
                      ? 'Restroom Survey Tag'
                      : assignEngagementType === 'retailer_alert'
                      ? 'Retailer Alert Tag'
                      : 'Review Engine Card'} URL
                  </p>
                </div>
              )}
            </div>

            {/* Card Location Label */}
            <div className="space-y-2">
              <Label className="text-white">Location Label <span className="text-[hsl(215,20%,45%)] font-normal text-xs">(shows in alerts)</span></Label>
              <Input
                value={cardLabel}
                onChange={(e) => setCardLabel(e.target.value)}
                placeholder="e.g., Aisle 20 Front, D021 Dairy Dept, Entrance Left"
                className="bg-[hsl(222,47%,11%)] border-[hsl(217,19%,27%)] text-white"
                data-testid="card-label-input"
              />
            </div>


            {/* Engagement Type Selector */}
            <div className="space-y-2">
              <Label className="text-white">Engagement Type</Label>
              <RadioGroup
                value={assignEngagementType}
                onValueChange={setAssignEngagementType}
                className="space-y-2"
                data-testid="assign-engagement-type-radio"
              >
                <div className={`flex items-center space-x-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                  assignEngagementType === 'guest_arrival'
                    ? 'border-green-500 bg-green-600/10'
                    : 'border-[hsl(217,19%,27%)] hover:border-[hsl(217,19%,40%)]'
                }`}>
                  <RadioGroupItem value="guest_arrival" id="assign-guest-arrival" />
                  <label htmlFor="assign-guest-arrival" className="flex-1 cursor-pointer flex items-center space-x-2 text-white">
                    <ClipboardCheck className="w-4 h-4 text-green-400" />
                    <span className="text-sm">Room Survey Tag (Guest Feedback)</span>
                  </label>
                </div>

                <div className={`flex items-center space-x-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                  assignEngagementType === 'restroom'
                    ? 'border-blue-500 bg-blue-600/10'
                    : 'border-[hsl(217,19%,27%)] hover:border-[hsl(217,19%,40%)]'
                }`}>
                  <RadioGroupItem value="restroom" id="assign-restroom" />
                  <label htmlFor="assign-restroom" className="flex-1 cursor-pointer flex items-center space-x-2 text-white">
                    <Droplets className="w-4 h-4 text-blue-400" />
                    <span className="text-sm">Restroom Survey Tag (Facility Feedback)</span>
                  </label>
                </div>

                <div className={`flex items-center space-x-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                  assignEngagementType === 'tap_engine'
                    ? 'border-amber-500 bg-amber-600/10'
                    : 'border-[hsl(217,19%,27%)] hover:border-[hsl(217,19%,40%)]'
                }`}>
                  <RadioGroupItem value="tap_engine" id="assign-tap-engine" />
                  <label htmlFor="assign-tap-engine" className="flex-1 cursor-pointer flex items-center space-x-2 text-white">
                    <Star className="w-4 h-4 text-amber-400" />
                    <span className="text-sm">Review Engine Card (External Reviews)</span>
                  </label>
                </div>

                <div className={`flex items-center space-x-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                  assignEngagementType === 'retailer_alert'
                    ? 'border-fuchsia-500 bg-fuchsia-600/10'
                    : 'border-[hsl(217,19%,27%)] hover:border-[hsl(217,19%,40%)]'
                }`}>
                  <RadioGroupItem value="retailer_alert" id="assign-retailer-alert" />
                  <label htmlFor="assign-retailer-alert" className="flex-1 cursor-pointer flex items-center space-x-2 text-white">
                    <AlertCircle className="w-4 h-4 text-fuchsia-400" />
                    <span className="text-sm">Retailer Alert Tag (In-Store Reporting)</span>
                  </label>
                </div>
              </RadioGroup>
            </div>

            {/* Review URL Configuration - Only for Review Engine cards */}
            {assignEngagementType === 'tap_engine' && (
              <div className="space-y-2 p-3 rounded-lg bg-amber-600/10 border border-amber-500/30">
                <Label className="text-amber-400 flex items-center">
                  <Star className="w-4 h-4 mr-2" />
                  Review Platform URL
                </Label>
                <Input
                  value={editReviewUrl}
                  onChange={(e) => handleUrlChange(e.target.value)}
                  placeholder="https://g.page/r/your-google-review-link"
                  className="bg-[hsl(222,47%,11%)] border-[hsl(217,19%,27%)] text-white"
                  data-testid="edit-review-url-input"
                />
                <p className="text-xs text-[hsl(215,20%,65%)]">
                  When guests tap this card, they will be redirected directly to this URL.
                  <br />
                  Leave empty to show platform selection page.
                </p>
                <div className="flex items-center space-x-2 mt-2">
                  <Label className="text-xs text-[hsl(215,20%,65%)]">Default Platform:</Label>
                  <Select value={editDefaultPlatform} onValueChange={handlePlatformChange}>
                    <SelectTrigger className="w-32 h-8 text-xs bg-[hsl(222,47%,11%)] border-[hsl(217,19%,27%)] text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="google">Google</SelectItem>
                      <SelectItem value="facebook">Facebook</SelectItem>
                      <SelectItem value="yelp">Yelp</SelectItem>
                      <SelectItem value="tripadvisor">TripAdvisor</SelectItem>
                      <SelectItem value="trustpilot">Trustpilot</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {/* Survey Questions Info - Only for guest_arrival cards */}
            {assignEngagementType === 'guest_arrival' && (
              <div className="space-y-2 p-3 rounded-lg bg-green-600/10 border border-green-500/30">
                <Label className="text-green-400 flex items-center">
                  <ClipboardCheck className="w-4 h-4 mr-2" />
                  Survey Questions
                </Label>
                {surveyQuestions.length > 0 ? (
                  <div className="space-y-2">
                    {surveyQuestions.slice(0, 4).map((q, idx) => (
                      <div key={q.id || idx} className="text-xs text-[hsl(215,20%,65%)] flex items-start">
                        <span className="mr-2 text-green-400">{idx + 1}.</span>
                        <span>{typeof q.text === 'object' ? q.text.en : q.text}</span>
                      </div>
                    ))}
                    <p className="text-xs text-[hsl(215,20%,50%)] mt-2">
                      To customize survey questions, go to Hotel Settings → Survey Configuration
                    </p>
                  </div>
                ) : (
                  <p className="text-xs text-[hsl(215,20%,65%)]">
                    Default survey questions will be used. Customize in Hotel Settings.
                  </p>
                )}
              </div>
            )}

            {/* Restroom Survey Questions Info - Only for restroom cards */}
            {assignEngagementType === 'restroom' && selectedCard?.organization_id && (
              <div className="space-y-2 p-3 rounded-lg bg-blue-600/10 border border-blue-500/30">
                <Label className="text-blue-400 flex items-center">
                  <Droplets className="w-4 h-4 mr-2" />
                  Restroom Survey Questions
                </Label>
                <p className="text-xs text-[hsl(215,20%,65%)] mb-2">
                  Restroom surveys can have 0 to 5 questions. All questions use 1-5 star ratings.
                </p>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => navigate(`/hotels/${selectedCard.organization_id}/restroom-survey-config`)}
                  className="border-blue-500/50 text-blue-400 hover:bg-blue-600/20 text-xs"
                  data-testid="configure-restroom-survey-btn"
                >
                  <Settings className="w-3 h-3 mr-1" />
                  Configure Restroom Survey Questions
                </Button>
              </div>
            )}

            {/* Alert Notification Assignment - For guest_arrival, restroom, AND retailer_alert cards */}
            {(assignEngagementType === 'guest_arrival' || assignEngagementType === 'restroom' || assignEngagementType === 'retailer_alert') && (
              <div className={`space-y-3 p-3 rounded-lg ${
                assignEngagementType === 'restroom' ? 'bg-blue-600/10 border border-blue-500/30' : 
                assignEngagementType === 'retailer_alert' ? 'bg-fuchsia-600/10 border border-fuchsia-500/30' :
                'bg-fuchsia-600/10 border border-fuchsia-500/30'
              }`}>
                <Label className={
                  assignEngagementType === 'restroom' ? 'text-blue-400 flex items-center' : 
                  assignEngagementType === 'retailer_alert' ? 'text-fuchsia-400 flex items-center' :
                  'text-fuchsia-400 flex items-center'
                }>
                  <UserPlus className="w-4 h-4 mr-2" />
                  {assignEngagementType === 'retailer_alert' ? 'In Store Alert Recipients' : 'Alert Recipients'}
                </Label>
                <p className="text-xs text-[hsl(215,20%,65%)]">
                  {assignEngagementType === 'retailer_alert' 
                    ? 'Select one or more people to receive in-store alert notifications for this tag.'
                    : 'Select one or more people to receive alert notifications for this card.'}
                </p>
                
                {/* Multi-select user checkboxes */}
                <div className="space-y-1 max-h-48 overflow-y-auto rounded-lg bg-[hsl(222,47%,11%)] p-2 border border-[hsl(217,19%,27%)]">
                  <p className="text-xs text-[hsl(215,20%,55%)] font-medium px-1 mb-1">Select recipients:</p>
                  {(() => {
                    // Combine hotel users + all system users (super admins), deduplicate
                    const seen = new Set();
                    const combinedUsers = [];
                    for (const u of [...allSystemUsers, ...hotelUsers]) {
                      if (!seen.has(u.id)) {
                        seen.add(u.id);
                        combinedUsers.push(u);
                      }
                    }
                    // Sort: super_admin first, then admins, then others
                    combinedUsers.sort((a, b) => {
                      const order = { super_admin: 0, admin: 1 };
                      return (order[a.role] ?? 2) - (order[b.role] ?? 2);
                    });
                    
                    if (combinedUsers.length === 0) {
                      return (
                        <p className="text-xs text-amber-400 px-1">No users found. Add users in hotel settings first.</p>
                      );
                    }
                    
                    return combinedUsers.map((user) => {
                      const isChecked = alertSelectedUserIds.includes(user.id);
                      const roleLabel = user.role === 'super_admin' ? 'Super Admin' : 
                                        user.hotel_role === 'admin' || user.role === 'admin' ? 'Admin' : 
                                        user.hotel_role || user.role || '';
                      return (
                        <label 
                          key={user.id} 
                          className={`flex items-center space-x-2 p-1.5 rounded cursor-pointer transition-colors ${
                            isChecked ? 'bg-[hsl(217,19%,20%)]' : 'hover:bg-[hsl(217,19%,15%)]'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setAlertSelectedUserIds([...alertSelectedUserIds, user.id]);
                              } else {
                                setAlertSelectedUserIds(alertSelectedUserIds.filter(id => id !== user.id));
                              }
                            }}
                            className="rounded border-[hsl(217,19%,40%)] text-fuchsia-500 focus:ring-fuchsia-500"
                          />
                          <span className="text-sm text-white flex-1">{user.name || user.email}</span>
                          {roleLabel && (
                            <span className={`text-xs px-1.5 py-0.5 rounded ${
                              user.role === 'super_admin' ? 'bg-purple-500/20 text-purple-300' : 'bg-blue-500/20 text-blue-300'
                            }`}>{roleLabel}</span>
                          )}
                        </label>
                      );
                    });
                  })()}
                </div>
                
                {alertSelectedUserIds.length > 0 && (
                  <p className="text-xs text-green-400">{alertSelectedUserIds.length} recipient{alertSelectedUserIds.length > 1 ? 's' : ''} selected</p>
                )}

                {/* Additional custom emails */}
                <div className="space-y-1.5 mt-2">
                  <p className="text-xs text-[hsl(215,20%,55%)] font-medium">Additional email addresses (optional):</p>
                  <Input
                    type="text"
                    value={alertAdditionalEmails}
                    onChange={(e) => setAlertAdditionalEmails(e.target.value)}
                    placeholder="email1@company.com, email2@company.com"
                    className="bg-[hsl(222,47%,11%)] border-[hsl(217,19%,27%)] text-white text-sm"
                    data-testid="alert-additional-emails-input"
                  />
                  <p className="text-xs text-[hsl(215,20%,55%)]">Separate multiple emails with commas</p>
                </div>

                {/* SMS Recipients (per-card) */}
                <div className="space-y-2 mt-3 border-t border-[hsl(217,19%,27%)] pt-3">
                  <p className="text-xs text-[hsl(215,20%,55%)] font-medium">SMS Recipients (optional):</p>
                  
                  {alertSmsRecipients.length > 0 && (
                    <div className="space-y-1">
                      {alertSmsRecipients.map((r, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 bg-[hsl(217,19%,13%)] rounded-lg" data-testid={`sms-recipient-${idx}`}>
                          <div className="flex items-center space-x-2 min-w-0">
                            <div className="w-2 h-2 rounded-full bg-emerald-400 flex-shrink-0" />
                            <span className="text-xs text-white truncate">{r.name || r.phone_number}</span>
                            <span className="text-[10px] text-[hsl(215,20%,50%)]">
                              {r.phone_number.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3')} &middot; {carrierList.find(c => c.id === r.carrier)?.name || r.carrier}
                            </span>
                          </div>
                          <button
                            onClick={() => removeSmsRecipient(idx)}
                            className="text-red-400 hover:text-red-300 p-1 flex-shrink-0"
                            data-testid={`remove-sms-${idx}`}
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="grid grid-cols-[1fr_auto] gap-2">
                    <div className="space-y-1.5">
                      <Input
                        value={smsNameInput}
                        onChange={(e) => setSmsNameInput(e.target.value)}
                        placeholder="Name (optional)"
                        className="bg-[hsl(222,47%,11%)] border-[hsl(217,19%,27%)] text-white text-xs h-8"
                        data-testid="sms-name-input"
                      />
                      <Input
                        value={smsPhoneInput}
                        onChange={handlePhoneChange}
                        placeholder="514-999-9999"
                        className="bg-[hsl(222,47%,11%)] border-[hsl(217,19%,27%)] text-white text-xs h-8"
                        data-testid="sms-phone-input"
                      />
                      <Select value={smsCarrierInput} onValueChange={setSmsCarrierInput}>
                        <SelectTrigger className="bg-[hsl(222,47%,11%)] border-[hsl(217,19%,27%)] text-white text-xs h-8" data-testid="sms-carrier-select">
                          <SelectValue placeholder="Select carrier" />
                        </SelectTrigger>
                        <SelectContent>
                          {carrierList.map((c) => (
                            <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Button
                      onClick={addSmsRecipient}
                      size="sm"
                      variant="outline"
                      className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10 self-end h-8"
                      data-testid="add-sms-recipient-btn"
                    >
                      <Plus className="w-3 h-3 mr-1" />
                      Add
                    </Button>
                  </div>

                  {alertSmsRecipients.length > 0 && (
                    <label className="flex items-center space-x-2 p-2 rounded bg-[hsl(217,19%,13%)] cursor-pointer">
                      <input
                        type="checkbox"
                        checked={applySmToAllCards}
                        onChange={(e) => setApplySmToAllCards(e.target.checked)}
                        className="rounded border-[hsl(217,19%,40%)] text-emerald-500 focus:ring-emerald-500"
                        data-testid="apply-sms-all-cards"
                      />
                      <span className="text-xs text-[hsl(215,20%,65%)]">Apply SMS recipients to all cards for this property</span>
                    </label>
                  )}
                </div>
              </div>
            )}

            {/* Card Branding Section */}
            {(assignEngagementType === 'guest_arrival' || assignEngagementType === 'restroom' || assignEngagementType === 'retailer_alert') && (
              <div className="space-y-4 p-3 rounded-lg bg-pink-600/10 border border-pink-500/30">
                <Label className="text-pink-400 flex items-center text-sm font-semibold">
                  <Image className="w-4 h-4 mr-2" />
                  Card Branding
                </Label>

                {/* ── LOGO ── */}
                <div className="space-y-2">
                  <p className="text-xs font-medium text-[hsl(215,20%,75%)] uppercase tracking-wider">Logo</p>
                  <p className="text-xs text-[hsl(215,20%,55%)]">
                    Custom logo at the top of this survey card.
                    {propertyHasLogo && !cardLogoUrl && (
                      <span className="block mt-0.5 text-pink-300">Using property-wide logo. Add one here to override.</span>
                    )}
                  </p>
                  {cardLogoUrl && (
                    <div className="relative inline-block">
                      <img 
                        src={cardLogoUrl.startsWith('http') ? cardLogoUrl : `${API_BASE}${cardLogoUrl}`}
                        alt="Survey logo"
                        className={`object-contain bg-white rounded-lg p-2 ${logoSizeClasses[cardLogoSize]}`}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                      <button
                        onClick={() => setCardLogoUrl('')}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-white hover:bg-red-600"
                        data-testid="remove-card-logo-btn"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                  <div className="space-y-2">
                    <label className="flex items-center justify-center w-full p-2 border border-dashed border-[hsl(217,19%,30%)] rounded-lg cursor-pointer hover:border-pink-400/50 transition-colors">
                      <input type="file" accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml" onChange={handleCardLogoUpload} className="hidden" data-testid="card-logo-upload-input" />
                      {uploadingCardLogo ? <Loader2 className="w-4 h-4 animate-spin text-pink-400 mr-2" /> : <Upload className="w-4 h-4 text-pink-400 mr-2" />}
                      <span className="text-xs text-[hsl(215,20%,65%)]">{uploadingCardLogo ? 'Uploading...' : 'Upload Logo'}</span>
                    </label>
                    <div className="flex gap-2">
                      <Input placeholder="Or paste image URL..." value={cardLogoUrlInput} onChange={(e) => setCardLogoUrlInput(e.target.value)} className="bg-[hsl(222,47%,11%)] border-[hsl(217,19%,27%)] text-white text-xs" data-testid="card-logo-url-input" />
                      <Button onClick={() => { if (cardLogoUrlInput.trim()) { setCardLogoUrl(cardLogoUrlInput.trim()); setCardLogoUrlInput(''); }}} disabled={!cardLogoUrlInput.trim()} size="sm" variant="outline" className="border-pink-500/30 text-pink-400 hover:bg-pink-500/10">Set</Button>
                    </div>
                  </div>
                  {cardLogoUrl && (
                    <div className="space-y-1">
                      <Label className="text-xs text-[hsl(215,20%,75%)]">Logo Size</Label>
                      <div className="grid grid-cols-4 gap-1">
                        {['small', 'medium', 'large', 'very_large'].map((size) => (
                          <button key={size} onClick={() => setCardLogoSize(size)} className={`p-1.5 text-xs rounded border transition-colors ${cardLogoSize === size ? 'border-pink-500 bg-pink-500/20 text-pink-300' : 'border-[hsl(217,19%,27%)] text-[hsl(215,20%,65%)] hover:border-pink-500/50'}`}>
                            {size === 'very_large' ? 'XL' : size.charAt(0).toUpperCase() + size.slice(1)}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* ── WALLPAPER ── */}
                <div className="space-y-2 pt-2 border-t border-pink-500/20">
                  <p className="text-xs font-medium text-[hsl(215,20%,75%)] uppercase tracking-wider">Wallpaper / Background Image</p>
                  <p className="text-xs text-[hsl(215,20%,55%)]">Full-page background behind the survey card.</p>
                  {cardWallpaperUrl && (
                    <div className="relative">
                      <img
                        src={cardWallpaperUrl.startsWith('http') ? cardWallpaperUrl : `${API_BASE}${cardWallpaperUrl}`}
                        alt="Wallpaper preview"
                        className="w-full h-24 object-cover rounded-lg border border-[hsl(217,19%,27%)]"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                      <button
                        onClick={() => setCardWallpaperUrl('')}
                        className="absolute top-1 right-1 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-white hover:bg-red-600"
                        data-testid="remove-wallpaper-btn"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                  <label className="flex items-center justify-center w-full p-2 border border-dashed border-[hsl(217,19%,30%)] rounded-lg cursor-pointer hover:border-pink-400/50 transition-colors">
                    <input type="file" accept="image/png,image/jpeg,image/gif,image/webp" onChange={handleWallpaperUpload} className="hidden" data-testid="wallpaper-upload-input" />
                    {uploadingWallpaper ? <Loader2 className="w-4 h-4 animate-spin text-pink-400 mr-2" /> : <Upload className="w-4 h-4 text-pink-400 mr-2" />}
                    <span className="text-xs text-[hsl(215,20%,65%)]">{uploadingWallpaper ? 'Uploading...' : (cardWallpaperUrl ? 'Replace Wallpaper' : 'Upload Wallpaper')}</span>
                  </label>
                  <p className="text-xs text-[hsl(215,20%,45%)] text-center">PNG, JPEG, GIF, WebP • Max 8MB</p>
                  <div className="flex gap-2">
                    <Input placeholder="Or paste image URL..." value={cardWallpaperUrlInput} onChange={(e) => setCardWallpaperUrlInput(e.target.value)} className="bg-[hsl(222,47%,11%)] border-[hsl(217,19%,27%)] text-white text-xs" data-testid="wallpaper-url-input" />
                    <Button onClick={() => { if (cardWallpaperUrlInput.trim()) { setCardWallpaperUrl(cardWallpaperUrlInput.trim()); setCardWallpaperUrlInput(''); }}} disabled={!cardWallpaperUrlInput.trim()} size="sm" variant="outline" className="border-pink-500/30 text-pink-400 hover:bg-pink-500/10">Set</Button>
                  </div>
                </div>

                {/* ── CARD SURFACE STYLE ── */}
                <div className="space-y-2 pt-2 border-t border-pink-500/20">
                  <p className="text-xs font-medium text-[hsl(215,20%,75%)] uppercase tracking-wider">Card Surface Style</p>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { id: 'frosted', label: 'Frosted', desc: 'Translucent + blur', icon: '◎' },
                      { id: 'clear', label: 'Clear', desc: 'See-through', icon: '○' },
                      { id: 'solid', label: 'Solid', desc: 'Fully opaque', icon: '●' },
                    ].map((s) => (
                      <button
                        key={s.id}
                        onClick={() => setCardStyle(s.id)}
                        data-testid={`card-style-${s.id}`}
                        className={`flex flex-col items-center gap-1 p-2.5 rounded-lg border transition-all ${
                          cardStyle === s.id
                            ? 'border-pink-500 bg-pink-500/20 ring-1 ring-pink-500/30'
                            : 'border-[hsl(217,19%,27%)] hover:border-pink-500/40'
                        }`}
                      >
                        <span className="text-lg">{s.icon}</span>
                        <span className={`text-xs font-medium ${cardStyle === s.id ? 'text-pink-300' : 'text-[hsl(215,20%,65%)]'}`}>{s.label}</span>
                        <span className="text-[10px] text-[hsl(215,20%,45%)]">{s.desc}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* ── BRANDING PREVIEW ── */}
                {cardWallpaperUrl && (
                  <div className="pt-2 border-t border-pink-500/20">
                    <p className="text-xs font-medium text-[hsl(215,20%,75%)] uppercase tracking-wider mb-2">Preview</p>
                    <div className="relative rounded-lg overflow-hidden h-48" data-testid="branding-preview">
                      {/* Wallpaper */}
                      <div className="absolute inset-0" style={{ backgroundImage: `url(${cardWallpaperUrl.startsWith('http') ? cardWallpaperUrl : `${API_BASE}${cardWallpaperUrl}`})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
                      {/* Overlay */}
                      <div className="absolute inset-0" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} />
                      {/* Card preview */}
                      <div className="absolute inset-0 flex items-center justify-center p-4">
                        <div className={`w-full max-w-[200px] rounded-xl p-4 ${
                          cardStyle === 'frosted' ? 'bg-white/5 backdrop-blur-md border border-white/10' :
                          cardStyle === 'clear' ? 'bg-white/0 border border-white/15' :
                          'bg-slate-900 border border-slate-800'
                        }`}>
                          {cardLogoUrl && (
                            <div className="flex justify-center mb-2">
                              <img src={cardLogoUrl.startsWith('http') ? cardLogoUrl : `${API_BASE}${cardLogoUrl}`} alt="Logo" className="h-8 object-contain" onError={(e) => { e.target.style.display = 'none'; }} />
                            </div>
                          )}
                          <div className="space-y-1">
                            <div className="h-2 bg-white/30 rounded w-3/4 mx-auto" />
                            <div className="h-2 bg-white/20 rounded w-1/2 mx-auto" />
                            <div className="h-6 bg-white/10 rounded mt-2" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* ── SAVE BRANDING ── */}
                <Button
                  onClick={saveCardBranding}
                  size="sm"
                  className="w-full bg-pink-600 hover:bg-pink-700 text-xs"
                  data-testid="save-card-branding-btn"
                >
                  <Image className="w-3 h-3 mr-1" />
                  Save Branding
                </Button>
              </div>
            )}

            {/* Location */}
            <div className="space-y-2">
              <Label className="text-white">Location</Label>
              <Select value={assignLocationId} onValueChange={setAssignLocationId}>
                <SelectTrigger className="bg-[hsl(222,47%,11%)] border-[hsl(217,19%,27%)] text-white">
                  <SelectValue placeholder="Select location" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {locations.map((loc) => (
                    <SelectItem key={loc.id} value={loc.id}>
                      {loc.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Employee Assignment - for tracking which staff member is responsible */}
            <div className="space-y-2">
              <Label className="text-white">
                {assignEngagementType === 'tap_engine' ? 'Assigned Staff (For Tracking)' : 'Assign to Employee (Optional)'}
              </Label>
              {assignEngagementType === 'tap_engine' && (
                <p className="text-xs text-[hsl(215,20%,65%)] mb-1">
                  Track which staff member or location this review card belongs to.
                </p>
              )}
              <Select value={assignEmployeeId} onValueChange={setAssignEmployeeId}>
                <SelectTrigger className="bg-[hsl(222,47%,11%)] border-[hsl(217,19%,27%)] text-white">
                  <SelectValue placeholder="Select employee" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {employees.map((emp) => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter className="mt-4">
            <Button
              variant="ghost"
              onClick={() => setShowAssignModal(false)}
              className="text-[hsl(215,20%,65%)]"
            >
              Cancel
            </Button>
            <Button
              className="bg-blue-600 hover:bg-blue-700"
              onClick={handleAssignCard}
              disabled={assigning}
              data-testid="assign-card-submit"
            >
              {assigning ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Save Assignment
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
